import React from 'react';
import { Card, Descriptions, Alert, Typography, Tag, Space } from 'antd';
import {
  EnvironmentOutlined,
  HomeOutlined,
  PhoneOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

interface ShippingAddress {
  // Rétrocompatibilité
  address?: string;

  // Données structurées
  formatted_address?: string;
  street_address?: string;
  locality?: string;
  administrative_area?: string;
  postal_code?: string;
  country?: string;

  // Compléments
  additional_info?: string;
  landmark?: string;
  delivery_instructions?: string;
  delivery_zone?: {
    id?: number;
    name?: string;
    delivery_fee?: number;
  } | null;
}

interface ShippingAddressDisplayProps {
  address: string | ShippingAddress;
}

const ShippingAddressDisplay: React.FC<ShippingAddressDisplayProps> = ({
  address,
}) => {
  // Si c'est une string simple (ancien format)
  if (typeof address === 'string') {
    return (
      <Alert
        message={
          <Space>
            <EnvironmentOutlined />
            <Text>{address}</Text>
          </Space>
        }
        type="info"
      />
    );
  }

  // Si c'est un objet avec uniquement "address" (ancien format JSON)
  if (address && !address.street_address && address.address) {
    return (
      <Alert
        message={
          <Space>
            <EnvironmentOutlined />
            <Text>{address.address}</Text>
          </Space>
        }
        type="info"
      />
    );
  }

  // Nouveau format structuré
  if (address && address.street_address) {
    return (
      <Card
        size="small"
        title={
          <Space>
            <EnvironmentOutlined />
            <Text strong>Adresse de livraison</Text>
          </Space>
        }
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label={<Text strong>Rue</Text>}>
            <Text>
              <HomeOutlined style={{ marginRight: 8 }} />
              {address.street_address}
            </Text>
          </Descriptions.Item>

          {address.delivery_zone?.name && (
            <Descriptions.Item label={<Text strong>Zone de livraison</Text>}>
              <Tag color="blue">{address.delivery_zone.name}</Tag>
              {address.delivery_zone.delivery_fee !== undefined && (
                <Text>
                  {" "}
                  {new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: "XOF",
                  }).format(Number(address.delivery_zone.delivery_fee || 0))}
                </Text>
              )}
            </Descriptions.Item>
          )}

          {address.locality && (
            <Descriptions.Item label={<Text strong>Localité</Text>}>
              <Tag color="cyan">{address.locality}</Tag>
            </Descriptions.Item>
          )}

          {address.postal_code && (
            <Descriptions.Item label={<Text strong>Code postal</Text>}>
              {address.postal_code}
            </Descriptions.Item>
          )}

          {address.country && (
            <Descriptions.Item label={<Text strong>Pays</Text>}>
              <Tag color="green">{address.country}</Tag>
            </Descriptions.Item>
          )}

          {address.additional_info && (
            <Descriptions.Item
              label={<Text strong>Complément d'adresse</Text>}
            >
              <Text type="secondary">{address.additional_info}</Text>
            </Descriptions.Item>
          )}

          {address.landmark && (
            <Descriptions.Item label={<Text strong>Point de repère</Text>}>
              <Text>
                <InfoCircleOutlined
                  style={{ marginRight: 8, color: '#1890ff' }}
                />
                {address.landmark}
              </Text>
            </Descriptions.Item>
          )}

          {address.delivery_instructions && (
            <Descriptions.Item
              label={<Text strong>Instructions de livraison</Text>}
            >
              <Alert
                message={address.delivery_instructions}
                type="warning"
                showIcon
                icon={<PhoneOutlined />}
                style={{ marginTop: 8 }}
              />
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Aperçu adresse complète */}
        <div
          style={{
            marginTop: '1rem',
            padding: '0.5rem',
            backgroundColor: '#f0f2f5',
            borderRadius: '4px',
          }}
        >
          <Text strong>Adresse complète : </Text>
          <br />
          <Text>
            {address.formatted_address ||
              `${address.street_address}, ${address.locality}${
                address.postal_code ? ' ' + address.postal_code : ''
              }, ${address.country || 'Burkina Faso'}`}
          </Text>
        </div>
      </Card>
    );
  }

  // Fallback si format inconnu
  return (
    <Alert
      message="Format d'adresse non reconnu"
      description={JSON.stringify(address)}
      type="warning"
    />
  );
};

export default ShippingAddressDisplay;
